// ================================================================
// 👥 AFFILIATE PERFORMANCE REPORT - Relatório de Performance de Afiliados
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AffiliatePerformanceReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartColors = useChartsColors();
  /**
   * Handle mudança de filtros de usuário
   */
  const handleUserFilterChange = (userFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters: {
          ...prev.advancedFilters,
          user_type: userFilters.user_type || undefined,
          user_id: userFilters.user_id || undefined
        }
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadPerformanceReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Carrega relatório com filtros específicos (usado quando filtros mudam)
   */
  const loadPerformanceReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('👥 Carregando Relatório de Performance com filtros...', filtersToUse);

      const result = await ReportsService.getAffiliatePerformanceReport({
        period: filtersToUse.period,
        user_type: filtersToUse.advancedFilters?.user_type,
        user_id: filtersToUse.advancedFilters?.user_id,
        product_id: filtersToUse.advancedFilters?.product_id,
        payment_method: filtersToUse.advancedFilters?.payment_method,
        sale_status: filtersToUse.advancedFilters?.sale_status,
        region: filtersToUse.advancedFilters?.region
      });

      if (result.success) {
        setPerformanceData(result.data);
        console.log('✅ Relatório de Performance carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Carrega dados do relatório
   */
  const loadPerformanceReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('👥 Carregando Relatório de Performance de Afiliados...', filters);

      const result = await ReportsService.getAffiliatePerformanceReport({
        period: filters.period,
        user_type: filters.advancedFilters?.user_type,
        user_id: filters.advancedFilters?.user_id,
        product_id: filters.advancedFilters?.product_id,
        payment_method: filters.advancedFilters?.payment_method,
        sale_status: filters.advancedFilters?.sale_status,
        region: filters.advancedFilters?.region
      });

      if (result.success) {
        setPerformanceData(result.data);
        console.log('✅ Relatório de Performance carregado:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Carrega relatório ao montar componente
   */
  useEffect(() => {
    loadPerformanceReport();
  }, [loadPerformanceReport]);

  /**
   * Handle mudança de período
   */
  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => ({
      ...prev,
      period: newPeriod
    }));
  };

  /**
   * Handle filtros avançados
   */
  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadPerformanceReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Handle exportar CSV
   */
  const handleExport = async () => {
    try {
      const blob = await ReportsService.exportCSV('affiliate-performance', filters);
      ReportsService.downloadCSV(blob, `relatorio-afiliados-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('❌ Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Esta funcionalidade será implementada em breve.');
    }
  };

  /**
   * Gráfico de Top 10 Afiliados
   */
  const topAffiliatesChart = performanceData?.charts?.topAffiliates ? {
    data: {
      labels: performanceData.charts.topAffiliates.map(item => item.affiliate_name),
      datasets: [
        {
          label: 'Receita (R$)',
          data: performanceData.charts.topAffiliates.map(item => item.total_revenue / 100),
          backgroundColor: '#007bff',
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `Receita: R$ ${context.parsed.x.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: chartColors
          },
          beginAtZero: true,
          ticks: {
            callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
          }
        },
        y: {
          grid: {
            color: chartColors
          }
        }

      }
    }
  } : null;

  /**
   * Colunas da tabela de afiliados
   */
  const affiliatesColumns = [
    { key: 'affiliate_name', label: 'Afiliado', sortable: true },
    { key: 'affiliate_email', label: 'Email', sortable: true },
    { key: 'joined_date', label: 'Desde', format: 'date', sortable: true },
    { key: 'sales_count', label: 'Total Vendas', sortable: true },
    { key: 'paid_sales_count', label: 'Vendas Pagas', sortable: true },
    { key: 'total_revenue', label: 'Receita Total', format: 'currency', sortable: true },
    { key: 'total_commissions', label: 'Comissões', format: 'currency', sortable: true },
    { key: 'avg_sale_value', label: 'Ticket Médio', format: 'currency', sortable: true },
    { 
      key: 'conversion_rate', 
      label: 'Taxa Conversão',
      sortable: true,
      render: (value) => `${(value || 0).toFixed(2)}%`
    }
  ];

  return (
    <MasterLayout>
      <Container fluid style={{ padding: '32px 24px', minHeight: '100vh' }}>
      {/* Cabeçalho com Botões */}
      <div style={{
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ flex: '1', minWidth: '300px' }}>
          <div style={{
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon icon="mdi:account-group" style={{ color: '#007bff', fontSize: '22px' }} />
            <span className='text-contrast' style={{
              fontSize: '22px',
              fontWeight: '700',
              // color: '#212529'
            }}>
              Relatório de Performance de Afiliados
            </span>
          </div>
          <p style={{
            fontSize: '16px',
            color: '#6c757d',
            margin: 0
          }}>
            Análise detalhada de performance individual por afiliado
          </p>
        </div>
        
        {/* Botões de Ação */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Button
            variant={Object.keys(filters.advancedFilters || {}).filter(k => k !== 'user_type' && k !== 'user_id' && filters.advancedFilters[k]).length > 0 ? 'primary' : 'outline-primary'}
            onClick={() => setShowFilterDrawer(true)}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:filter" style={{ fontSize: '18px' }} />
            Filtros Avançados
          </Button>

          <Button
            variant="outline-success"
            onClick={handleExport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:file-excel" style={{ fontSize: '18px' }} />
            Exportar CSV
          </Button>

          <Button
            variant="primary"
            onClick={loadPerformanceReport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon 
              icon="mdi:refresh" 
              style={{ fontSize: '18px' }}
              className={loading ? 'spin' : ''}
            />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        period={filters.period}
        onPeriodChange={handlePeriodChange}
        onApplyAdvancedFilters={handleAdvancedFilters}
        onRefresh={loadPerformanceReport}
        onExport={handleExport}
        loading={loading}
        advancedFilters={filters.advancedFilters}
        onUserFilterChange={handleUserFilterChange}
        showExport={false}
      />

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        onApply={(newFilters) => {
          setShowFilterDrawer(false);
          handleAdvancedFilters(newFilters);
        }}
        currentFilters={filters.advancedFilters}
      />

      {/* Erro */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
          {error}
        </Alert>
      )}

      {/* KPIs */}
      <Row className="g-4 mb-5">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Afiliados Ativos"
            value={loading ? '...' : (performanceData?.kpis?.active_affiliates || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : 'Com vendas no período'}
            icon="mdi:account-multiple"
            variant="blue"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Vendas Geradas"
            value={loading ? '...' : (performanceData?.kpis?.total_sales || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : `${performanceData?.kpis?.avg_sales_per_affiliate || 0} vendas/afiliado`}
            icon="mdi:cart"
            variant="green"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Receita Total"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(performanceData?.kpis?.total_revenue || 0)}
            subtitle={loading ? '' : 'Por afiliados'}
            icon="mdi:cash"
            variant="orange"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Comissões Pagas"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(performanceData?.kpis?.total_commissions_paid || 0)}
            subtitle={loading ? '' : CurrencyFormatter.centsToFormatted(performanceData?.kpis?.avg_commission_per_sale || 0) + ' por venda'}
            icon="mdi:account-cash"
            variant="purple"
          />
        </Col>
      </Row>

      {/* Gráfico de Top 10 Afiliados */}
      <Row className="g-4 mb-5">
        <Col xs={12}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minHeight: '500px'
          }}>
            <Card.Body>
              <span className='text-contrast' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:trophy" style={{ color: '#ffc107' }} />
                Top 10 Afiliados por Receita
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : topAffiliatesChart ? (
                <div style={{ height: '400px' }}>
                  <Bar data={topAffiliatesChart.data} options={topAffiliatesChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Afiliados */}
      <Row>
        <Col xs={12}>
          <span className='text-contrast' style={{
            fontSize: '18px',
            fontWeight: '600',
            // color: '#212529',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon icon="mdi:format-list-bulleted" style={{ color: '#6c757d' }} />
            Lista Completa de Afiliados (últimos 100)
          </span>
          <SmartTable
            data={performanceData?.affiliatesList || []}
            columns={affiliatesColumns}
            loading={loading}
            searchable={true}
            itemsPerPage={20}
            emptyMessage="Nenhum afiliado encontrado"
          />
        </Col>
      </Row>
      </Container>
    </MasterLayout>
  );
};

export default AffiliatePerformanceReport;

